import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Table from '../ogComponent/table';
import { IconPencilMinus } from '@tabler/icons-react';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import Spinner from '../ogComponent/spinner';

export default function LeaveBalance() {
    const [leaveData, setLeaveData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [balanceLeave, setBalanceLeave] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const toast = useRef(null);

    // Access permissions array from Redux store
    const permissions = useSelector((state) => state.emp.permissions);

    const pagePermissions = ["showbalanceleavesID", "editbalanceleavesID"];

    const header = [
        { name: "Si_No", label: "#", active: true },
        { name: "employee_code", label: "User Code", active: true },
        { name: "employee", label: "Employee", active: true },
        { name: "position", label: "Job Position", active: true },
        { name: "leaves", label: "Leaves", active: true },
        { name: "edit", label: "", active: permissions.includes("editbalanceleavesID") },
    ];

    // Handle page change
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    useEffect(() => {
        if (permissions.includes("showbalanceleavesID")) {
            fetchLeaveBalance();
        } else {
            setLoading(false);
        }
    }, [permissions]);

    const fetchLeaveBalance = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/leavemanagement/balance-leave/`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            // Transform API response to match table structure
            const formattedData = response.data.map((item, index) => ({
                id: item.employee.id,
                Si_No: index + 1,
                employee_code: item.employee.employee_code || 'N/A',
                employee: (
                    <td
                        className="d-flex gap-3 align-items-center px-0"
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <img
                            style={{
                                width: "25px",
                                height: "25px",
                                flexShrink: 0,
                                borderRadius: "50%",
                            }}
                            src={item.employee?.profile_photo || "/assets/user.png"}
                            alt="user img"
                        />
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.employee?.name || "N/A"}
                        </div>
                    </td>
                ),
                position: item.employee.job_position?.job_title || 'N/A',
                leaves: item.balance_leave,
                employee_id: item.employee.id,
                edit: permissions.includes("editbalanceleavesID") ? (
                    <IconPencilMinus style={{ cursor: 'pointer' }} onClick={(e) => handleEditClick(e, item)} />
                ) : null,
            }));

            setLeaveData(formattedData);
        } catch (err) {
            console.error("Error fetching leave balance:", err);
            setError("Failed to load leave balance data");
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (e, item) => {
        if (permissions.includes("editbalanceleavesID")) {
            e.stopPropagation();
            setSelectedEmployee(item.employee?.id);
            setBalanceLeave(item.balance_leave.toString());
            setShowModal(true);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEmployee(null);
        setBalanceLeave('');
    };

    const handleUpdateLeave = async () => {
        if (!permissions.includes("editbalanceleavesID")) return;

        try {
            const accessToken = localStorage.getItem('accessToken');
            await axios.post(
                `${process.env.REACT_APP_API}/api/leavemanagement/balance-leave/`,
                {
                    employee_id: selectedEmployee,
                    balance_leave: parseFloat(balanceLeave)
                },
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            // Show success toast
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: "Balance Updated Successfully",
                life: 3000,
            });

            // Close modal and refresh data
            handleCloseModal();
            fetchLeaveBalance();
        } catch (err) {
            console.error("Error updating leave balance:", err);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: "Failed to update balance leave",
                life: 3000,
            });
        }
    };

    const handleRowClick = () => {
        console.log("Row Clicked");
    };

    return (
        <div className="todayLeavesContainer w-100 d-flex flex-column p-3 p-md-4 gap-3 gap-md-4">
            <Toast ref={toast} position="bottom-right" />
            <h3>Balance Leaves</h3>

            {loading ? (
                <Spinner />
            ) : permissions.includes("showbalanceleavesID") ? (
                error ? (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                ) : leaveData.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-end position-relative" style={{ height: "50vh" }}>
                        <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                            backgroundImage: 'url(/assets/file.png)',
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            opacity: 0.3,
                        }}></div>
                        <p className="m-0 fw-light fs-5 position-relative">No Data Found!</p>
                    </div>
                ) : (
                    <Table
                        header={header}
                        product={leaveData}
                        onRowClick={handleRowClick}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                )
            ) : (
                <p>No permission to view leave balances.</p>
            )}

            {showModal && permissions.includes("editbalanceleavesID") && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" aria-labelledby="editModalLabel" aria-modal="true">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="editModalLabel">Edit Balance Leave</h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="balanceLeave" className="form-label">Balance Leave</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="balanceLeave"
                                        value={balanceLeave}
                                        onChange={(e) => setBalanceLeave(e.target.value)}
                                        min="0"
                                        step="0.5"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleUpdateLeave}>Update</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}